import React from 'react';
import { useShow } from '@refinedev/core';
import { useTranslation } from 'react-i18next';
import { Show, DateField, ListButton } from '@refinedev/mui';
import { Typography, Stack, Chip, Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import { ErrorBoundary } from '@/shared/components/ui';
import { getKey, ParticipantProfile } from '@aphp-ema/common/participant';
import { getDisplayName } from '@/features/participants/utils/chronic-illness';

export const ParticipantShow = () => {
  const { t } = useTranslation();
  const { t: tChronicIllnesses } = useTranslation('chronicIllnesses');
  const { id } = useParams();

  const {
    query: { data, isLoading },
  } = useShow<ParticipantProfile>({
    resource: 'admin/participants',
    id,
  });

  const record = data?.data;

  return (
    <ErrorBoundary>
      <Show
        isLoading={isLoading}
        recordItemId={id}
        canDelete={false}
        title={t('pages.participants.show.title')}
        headerButtons={({ listButtonProps }) => (
          <>
            <ListButton {...listButtonProps} />
          </>
        )}
      >
        <Stack gap={1}>
          <Typography variant="body1" fontWeight="bold">
            {t('pages.participants.fields.emaId')}
          </Typography>
          <Typography variant="body2">{record?.emaId}</Typography>

          <Typography variant="body1" fontWeight="bold" mt={2}>
            {t('pages.participants.fields.email')}
          </Typography>
          <Typography variant="body2">{record?.email}</Typography>

          <Typography variant="body1" fontWeight="bold" mt={2}>
            {t('pages.participants.fields.phone')}
          </Typography>
          <Typography variant="body2">{record?.phone || '-'}</Typography>

          <Typography variant="body1" fontWeight="bold" mt={2}>
            {t('pages.participants.fields.birthdate')}
          </Typography>
          <Typography variant="body2">
            {record?.patientData?.birthdate
              ? new Date(record.patientData.birthdate).toLocaleDateString()
              : '-'}
          </Typography>

          <Typography variant="body1" fontWeight="bold" mt={2}>
            {t('pages.participants.fields.gender')}
          </Typography>
          <Typography variant="body2">
            {record?.patientData?.gender
              ? t(`pages.participants.gender.${record.patientData.gender}`)
              : '-'}
          </Typography>

          <Typography variant="body1" fontWeight="bold" mt={2}>
            {t('pages.participants.fields.chronicIllnesses')}
          </Typography>
          <Box>
            {record?.patientData?.chronicIllnesses?.length ? (
              record.patientData.chronicIllnesses.map((illness) => (
                <Chip
                  key={getKey(illness)}
                  label={getDisplayName(illness, tChronicIllnesses)}
                  color="primary"
                  size="small"
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
              ))
            ) : (
              <Typography variant="body2">-</Typography>
            )}
          </Box>

          <Typography variant="body1" fontWeight="bold" mt={2}>
            {t('pages.participants.fields.createdAt')}
          </Typography>
          <DateField value={record?.createdAt} />
        </Stack>
      </Show>
    </ErrorBoundary>
  );
};
